import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { ListFilters } from '../../../../interfaces/stockist.interface';
import { buildHeadCells } from '../../../../utils/methods';
import {
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
  GENERIC_LOOKUP_KEYS,
  STATUS,
} from '../../../../utils/appConstant';
import useSorting from '../../../../hooks/useSorting';
import usePagination from '../../../../hooks/usePagination';
import useUrlParams from '../../../../hooks/useUrlParams';
import useFetch from '../../../../hooks/useFetch';
import StockistService from '../../../../services/stockist.service';
import CommonService from '../../../../services/common.service';
import { SortOrder, Modules, AuthRoutes } from '../../../../enums';

const List: React.FC = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>({
    searchText: '',
    status: STATUS.active,
  });

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    status: '',
  });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.name,
      VALIDATION_FIELD_MAPPING.zone,
      VALIDATION_FIELD_MAPPING.city,
      VALIDATION_FIELD_MAPPING.state,
      VALIDATION_FIELD_MAPPING.email,
      VALIDATION_FIELD_MAPPING.status,
      VALIDATION_FIELD_MAPPING.updated_at,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'stockistList'
  );

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.name,
  });
  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: AuthRoutes.stockists,
    uri: Modules.resources + AuthRoutes.stockists,
    perPage,
    page,
    order,
    orderBy,
  });

  const {
    total,
    data: stockists,
    loading,
  } = useFetch({
    service: StockistService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.stockistList?.[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      status: filters.status === STATUS.active ? '1' : filters.status === STATUS.inactive ? '0' : '',
    },
    dependency: [
      order,
      orderBy,
      page,
      perPage,
      filters.searchText,
      filters.status,
    ],
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: [],
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const handleFilterschange = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.name);
    setFilters(newFilters);
  }, []);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const headerSection = <TableHeaderSection total={total} />;

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection,
      row: Row,
      data: stockists || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('stockists.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    };
  }, [
    headCells,
    order,
    orderBy,
    page,
    perPage,
    total,
    loading,
    stockists,
    setPage,
    setPerPage,
    setOrder,
    setOrderBy,
  ]);

  return (
    <>
      <Header />

      <Filters
        initialFilters={filters}
        onFilterChange={handleFilterschange}
        status={status}
        allStatus={allStatus}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
      />

      <TableBox tableValues={values} />
    </>
  );
};

export default List;
